import * as React from "react";
import { useParams, useHistory } from "react-router";
import { useApiService, useTl } from "../../../../hooks";
import { PedigreeRequestApi, RequestApi, ERequestType, ERequestState } from "../../../../api";
import { useApiEffect } from "nsitools-react";
import {
  PageBase,
  RequestStickyMenu,
  BackButton,
  ConditionTab,
  LinkedSubFilesTab,
  ProtectedTabs,
  RequestHistoryTab,
} from "../../../../components";
import { ETLCodes } from "../../../../locales";
import { ERoutes } from "../../../../ERoutes";
import { Icon, Spinner, SpinnerSize, Tab } from "@blueprintjs/core";
import { PedigreeRequestDetailPage } from "./PedigreeRequestDetailPage";
import { useGlobalData } from "../../../../contexts";
import { BillTab } from "../../BillTab";
import { MailLogListPage } from "../../../mail-log/MailLogListPage";

export interface IPedigreeRequestItemPageProps {}
export const PedigreeRequestItemPage: React.FunctionComponent<IPedigreeRequestItemPageProps> = (props) => {
  const {
    id,
    tab,
    state,
    masterRequestId = "0",
  } = useParams<{
    id: string;
    tab: string;
    state: string;
    masterRequestId: string;
  }>();
  const history = useHistory();
  const api = useApiService(PedigreeRequestApi);
  const requestApi = useApiService(RequestApi);
  const { currentLanguage } = useGlobalData();
  const { t } = useTl();

  const [requestInfo, requestInfoLoading, , refreshRequestInfo] = useApiEffect(
    () => requestApi.getRequestInfo(ERequestType.PedigreeRequest, +id, +masterRequestId, currentLanguage.languageId),
    [requestApi, id, masterRequestId, currentLanguage],
  );

  const refreshRequest = React.useCallback(() => {
    refreshRequestInfo();
  }, [refreshRequestInfo]);

  const closeRequest = React.useCallback(async () => {
    await api.closePedigreeRequest(requestInfo?.parentRequestId);
  }, [api, requestInfo]);

  return (
    <PageBase
      breadCrumbs={[
        {
          text: t(ETLCodes.RISHRequests),
          route: ERoutes.rish,
        },
        {
          text:
            +id > 0 ? (
              requestInfoLoading ? (
                <Spinner size={SpinnerSize.SMALL} />
              ) : (
                requestInfo.requestNumber
              )
            ) : (
              t(ETLCodes.New)
            ),
        },
      ]}
      actionHistory={
        +id > 0 &&
        !requestInfoLoading && {
          tlCode: ETLCodes.ActionHistoryRish,
          name: requestInfo.requestNumber,
          id: +id,
        }
      }
      stickyContent={() => {
        if (+id > 0 && !requestInfoLoading) {
          return (
            <RequestStickyMenu
              requestId={requestInfo.parentRequestId}
              refreshFunc={refreshRequest}
              closeFunc={closeRequest}
            />
          );
        } else {
          return null;
        }
      }}
    >
      <ProtectedTabs
        id="TabsPedigreeRequest"
        onChange={(newTabId) => history.push(`${ERoutes.rish}/${+id}/${newTabId}/${state}`)}
        selectedTabId={tab}
        renderActiveTabPanelOnly
      >
        <BackButton backRoute={ERoutes.rish} />
        <Tab
          id="detail"
          title={
            <div>
              {t(ETLCodes.Detail)} {requestInfo?.hasWarning && <Icon icon="warning-sign" intent="danger" />}
            </div>
          }
          panel={<PedigreeRequestDetailPage pedigreeRequestId={+id} editMode={state === "edit"} />}
        />
        <Tab id="verifications" title={t(ETLCodes.Verifications)} disabled />
        <Tab
          id="conditions"
          title={t(ETLCodes.Conditions)}
          panel={
            requestInfoLoading ? (
              <Spinner />
            ) : (
              <ConditionTab
                requestId={+requestInfo.parentRequestId}
                editMode={state === "edit"}
                requestState={ERequestState.REQUESTED}
                statusETLCode={ETLCodes.ConditionsForBillingStatus}
              />
            )
          }
          disabled={+id <= 0}
        />
        <Tab
          id="subrequests"
          title={t(ETLCodes.SubRequests, {
            count: requestInfoLoading ? `` : `(${requestInfo.subRequestCount})`,
          })}
          panel={
            requestInfoLoading ? (
              <Spinner />
            ) : (
              <LinkedSubFilesTab requestInfo={requestInfo} editMode={state === "edit"} />
            )
          }
          disabled={+id <= 0}
        />
        <Tab
          id="billing"
          title={t(ETLCodes.Billing)}
          panel={
            <BillTab
              requestId={requestInfo?.parentRequestId}
              disabled={requestInfo?.requestStateCode !== ERequestState.CANCELLED}
            />
          }
          disabled={requestInfo?.requestStateCode === ERequestState.REQUESTED}
        />
        <Tab
          id="history"
          title={t(ETLCodes.History)}
          panel={requestInfoLoading ? <Spinner /> : <RequestHistoryTab requestId={requestInfo.parentRequestId} />}
          disabled={+id <= 0}
        />
        <Tab
          id="mailing"
          title={t(ETLCodes.Email)}
          panel={requestInfoLoading ? <Spinner /> : <MailLogListPage requestId={requestInfo?.parentRequestId} />}
          disabled={+id <= 0}
        />
      </ProtectedTabs>
    </PageBase>
  );
};
